import { action, observable } from 'mobx'
import { InternalMessagesRequest } from '../requests/InternalMessagesRequest'
import { FileRequest } from '../requests/FileRequest'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

import { ASSIGNEE_TYPE } from '../constants'

class InternalMessageStore {
  @observable internalMessageList = []
  @observable internalMessageDetail = {}
  @observable internalMessageListTotalCount = 0
  @observable unreadInternalMessageCount = 0
  @observable replyInternalMessages = []
  @observable pageSize = 30
  @observable pageIndex = 0
  @observable keyword = ''
  @observable queryParams = {
    page: 0,
    size: 10,
  }

  @action setQueryParams = queryParams => {
    this.queryParams = queryParams
  }

  @action setFilter = (filterName, filterValue) => {
    if (typeof filterName !== 'string') return
    this[filterName] = filterValue
  }
  @action getInternalMessageReceiveList = queryStringParse => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.getReceiveList(
        queryStringParse.page,
        queryStringParse.size,
        queryStringParse.keyword
      )
        .then(response => {
          this.internalMessageList = response.data?.data
          this.internalMessageList?.map(item => {
            const receiverList =
              item.receiver?.map((el, index) => ({
                id: index,
                full_name: el.fullname,
                assignee_type: ASSIGNEE_TYPE.USER,
                last_date_read: el.updatedTime,
              })) ?? []
            item.receiverList = receiverList
            return item
          })
          this.internalMessageListTotalCount =
            response.data?.total_elements || 0
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action getInternalMessageSendList = queryStringParse => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.getSendList(
        queryStringParse.page,
        queryStringParse.size,
        queryStringParse.keyword
      )
        .then(response => {
          this.internalMessageList = response.data.data
          this.internalMessageList.map(item => {
            const receiverList =
              item.receiver?.map((el, index) => ({
                id: index,
                full_name: el.fullname,
                assignee_type: ASSIGNEE_TYPE.USER,
                last_date_read: el.updatedTime,
              })) ?? []
            item.receiverList = receiverList
            return item
          })
          this.internalMessageListTotalCount =
            response.data?.total_elements || 0
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action getReceiveMessageById = id => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.getReceiveMessageById(id)
        .then(response => {
          this.internalMessageDetail = response.data
          if (response.data.reply_internal_messages?.length > 0) {
            this.replyInternalMessages = response.data.reply_internal_messages.sort(
              (a, b) => (a.created_time > b.created_time ? 1 : -1)
            )
          }
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action getSendMessageById = id => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.getSendMessageById(id)
        .then(response => {
          this.internalMessageDetail = response.data
          if (response.data.reply_internal_messages?.length > 0) {
            this.replyInternalMessages = response.data.reply_internal_messages.sort(
              (a, b) => (a.created_time > b.created_time ? 1 : -1)
            )
          }
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action create = data => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.create(data)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }
  @action getInternalMessageUserCodeByInternalMessageCode = id => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.getInternalMessageUserCodeByInternalMessageCode(
        id
      )
        .then(response => {
          resolve(response.data.internal_message_user_code)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action updateTitle = (code, title) => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.updateTitle(code, title)
        .then(response => {
          this.internalMessageDetail.title = title
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action updateContent = (code, content, file_ids) => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.updateContent(code, content, file_ids)
        .then(response => {
          if (this.internalMessageDetail.code === code) {
            this.internalMessageDetail.content = content
          }
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action softDelete = code => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.softDelete(code)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action updateReceivers = (code, receivers) => {
    return new Promise((resolve, reject) => {
      InternalMessagesRequest.updateReceivers(code, receivers)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action downloadAllZip = async () => {
    try {
      const zip = new JSZip()
      const filesToDownload = []

      // Main message files
      if (this.internalMessageDetail.file_ids?.length > 0) {
        this.internalMessageDetail.file_ids.forEach(file => {
          filesToDownload.push({ id: file.file_id, name: file.name })
        })
      }

      // Comment files
      if (this.replyInternalMessages?.length > 0) {
        this.replyInternalMessages.forEach(msg => {
          if (msg.file_ids?.length > 0) {
            msg.file_ids.forEach(file => {
              filesToDownload.push({ id: file.file_id, name: file.name })
            })
          }
        })
      }

      if (filesToDownload.length === 0) {
        throw new Error('Không có tài liệu nào để tải xuống')
      }

      const fetchFilePromises = filesToDownload.map(async file => {
        try {
          const res = await FileRequest.downloadAttachment(file.id)
          zip.file(file.name, res.data)
        } catch (error) {
          console.error(`Error downloading file ${file.name}:`, error)
        }
      })

      await Promise.all(fetchFilePromises)
      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, `${this.internalMessageDetail.title || 'TaiLieu'}.zip`)
    } catch (error) {
      throw error
    }
  }

  /** Clear */
  @action clearMessageStore = () => {
    this.internalMessageList.length = 0
    this.internalMessageListTotalCount = 0
    this.replyInternalMessages = []
    this.unreadInternalMessageCount = 0
    this.internalMessageDetail = {}
    this.pageIndex = 0
    this.pageSize = 30
  }

  @action clearInternalMessageList = () => {
    this.internalMessageList = []
  }

  @action clearQueryParams = () => {
    this.queryParams = {
      page: 0,
      size: 10,
    }
  }
}

export default new InternalMessageStore()
